import React, { useState, useEffect, useRef } from 'react';
import './App.css';

// URL del API en Railway
const API_URL = 'https://web-production-fd98.up.railway.app/api/regencias/analizar';
const GEOCODING_URL = 'https://web-production-fd98.up.railway.app/api/geocoding/buscar';

// ═══════════════════════════════════════════════════════════════
// ASTROENGINE PRO INTEGRATION
// ═══════════════════════════════════════════════════════════════
const HUB_URL = 'https://astroenginepro.com';
const LOGIN_URL = HUB_URL + '/login.html?return=index';
const DASHBOARD_URL = HUB_URL + '/dashboard.html';

const DEMO_DATA = {
  shakira: { nombre: "Shakira", fecha: "1977-02-02", hora: "10:08", ciudad: "Barranquilla", lat: 10.9639, lon: -74.7964, tz: -5 },
  thalia: { nombre: "Thalía", fecha: "1971-08-26", hora: "14:30", ciudad: "Ciudad de México", lat: 19.4326, lon: -99.1332, tz: -6 },
  harry: { nombre: "Príncipe Harry", fecha: "1984-09-15", hora: "16:20", ciudad: "Londres", lat: 51.5074, lon: -0.1278, tz: 0 }
};

// Obtener parámetros de URL
const getUrlParams = () => {
  const params = new URLSearchParams(window.location.search);
  const demoKey = params.get('demo')?.toLowerCase();
  let userData = (params.get('nombre') || params.get('fecha') || params.get('hora') || params.get('ciudad')) ? {
    nombre: params.get('nombre') || '',
    fecha: params.get('fecha') || '',
    hora: params.get('hora') || '',
    ciudad: params.get('ciudad') || '',
    pais: params.get('pais') || '',
    lat: parseFloat(params.get('lat') || '0'),
    lon: parseFloat(params.get('lon') || '0'),
    tz: parseFloat(params.get('tz') || params.get('timezone') || '0'),
    plan: params.get('plan') || '',
    vence: params.get('vence') || '',
    email: params.get('email') || ''
  } : null;

  if (userData) {
    // Llegaron datos frescos por URL: los guardamos para sobrevivir a un refresh
    // posterior (la URL se limpia y ya no los tendrá disponibles).
    try { sessionStorage.setItem('astroengine_userdata', JSON.stringify(userData)); } catch (e) {}
  } else {
    // No hay datos en la URL (típico tras un refresh, porque ya la limpiamos):
    // intentamos recuperarlos de sessionStorage.
    try {
      const saved = sessionStorage.getItem('astroengine_userdata');
      if (saved) userData = JSON.parse(saved);
    } catch (e) {}
  }

  return {
    demo: demoKey,
    readonly: params.get('readonly') === '1',
    admin: params.get('admin') === 'Lima2026',
    celebData: DEMO_DATA[demoKey] || null,
    userData,
    activeData: DEMO_DATA[demoKey] || userData
  };
};

const urlParams = getUrlParams();

// Limpiar datos personales de la barra de direcciones una vez leídos en memoria
// (ya quedaron respaldados en sessionStorage por getUrlParams para sobrevivir a un refresh).
if (urlParams.userData && typeof window !== 'undefined') {
  window.history.replaceState(null, '', window.location.pathname + window.location.hash);
}
// ═══════════════════════════════════════════════════════════════

// Ciudades predefinidas (acceso rápido)
const CIUDADES = [
  { nombre: 'Lima', lat: -12.0464, lon: -77.0428, tz: -5 },
  { nombre: 'México DF', lat: 19.4326, lon: -99.1332, tz: -6 },
  { nombre: 'Buenos Aires', lat: -34.6037, lon: -58.3816, tz: -3 },
  { nombre: 'Madrid', lat: 40.4168, lon: -3.7038, tz: 1 },
  { nombre: 'Bogotá', lat: 4.7110, lon: -74.0721, tz: -5 },
  { nombre: 'Santiago', lat: -33.4489, lon: -70.6693, tz: -4 },
  { nombre: 'New York', lat: 40.7128, lon: -74.0060, tz: -5 },
];

// Símbolos planetarios
const SIMBOLOS = {
  'Sol': '☉', 'Luna': '☽', 'Mercurio': '☿', 'Venus': '♀',
  'Marte': '♂', 'Júpiter': '♃', 'Saturno': '♄'
};

// Interpretaciones de planetas
const INTERPRETACIONES = {
  'Sol': {
    titulo: 'El Líder',
    rasgos: 'Voluntad fuerte, creatividad, necesidad de brillar y ser reconocido. Dignidad, autoridad natural, generosidad.',
    vocacion: 'Liderazgo, gobierno, artes escénicas, dirección, emprendimiento.'
  },
  'Luna': {
    titulo: 'El Alma Emocional',
    rasgos: 'Sensibilidad, intuición, receptividad, necesidad de seguridad emocional. Conexión con el público y las masas.',
    vocacion: 'Cuidado, nutrición, hotelería, navegación, comercio popular.'
  },
  'Mercurio': {
    titulo: 'El Comunicador',
    rasgos: 'Inteligencia ágil, curiosidad, versatilidad, habilidad verbal y escrita. Mente analítica que busca entender y conectar información.',
    vocacion: 'Comercio, escritura, enseñanza, tecnología, intermediación, análisis de datos.'
  },
  'Venus': {
    titulo: 'El Armonizador',
    rasgos: 'Amor por la belleza, diplomacia, encanto, sensualidad. Busca placer, armonía y conexión afectiva.',
    vocacion: 'Arte, música, moda, decoración, relaciones públicas, joyería.'
  },
  'Marte': {
    titulo: 'El Guerrero',
    rasgos: 'Energía, coraje, iniciativa, competitividad. Impulso para actuar y conquistar.',
    vocacion: 'Militar, cirugía, ingeniería, deportes, metalurgia.'
  },
  'Júpiter': {
    titulo: 'El Benefactor',
    rasgos: 'Optimismo, expansión, generosidad, fe. Busca significado, crecimiento y abundancia.',
    vocacion: 'Leyes, religión, filosofía, educación superior, comercio exterior.'
  },
  'Saturno': {
    titulo: 'El Maestro',
    rasgos: 'Disciplina, responsabilidad, paciencia, estructura. Sabiduría a través de la experiencia y el tiempo.',
    vocacion: 'Arquitectura, agricultura, minería, administración, antigüedades.'
  }
};

// Interpretación del Dispositor
const INTERP_DISPOSITOR = {
  'Sol': 'Todo en tu vida sirve para expresar tu individualidad y brillar con luz propia.',
  'Luna': 'Todo en tu vida sirve para nutrir, cuidar y conectar emocionalmente.',
  'Mercurio': 'Todo en tu vida sirve para aprender, comunicar y conectar ideas.',
  'Venus': 'Todo en tu vida sirve para crear armonía, belleza y relaciones significativas.',
  'Marte': 'Todo en tu vida sirve para actuar, conquistar y defender lo que valoras.',
  'Júpiter': 'Todo en tu vida sirve para expandirte, crecer y encontrar significado.',
  'Saturno': 'Todo en tu vida sirve para construir estructuras duraderas y dejar un legado.'
};

// Instrucciones del módulo
const MODULE_DOCS = urlParams.activeData
  ? `Regencias Planetarias

Datos cargados automáticamente.
Pulsa "Calcular Regencias" para ver el análisis.

ALMUTEN = Planeta más fuerte de tu carta
DISPOSITOR = Planeta que domina a todos

Metodología: Tradición Clásica`
  : `Regencias Planetarias

1. Ingresa los datos de nacimiento
2. Selecciona la ciudad
3. Clic en "Calcular Regencias"

ALMUTEN = Planeta más fuerte de tu carta
DISPOSITOR = Planeta que domina a todos

Metodología: Tradición Clásica`;

// ═══════════════════════════════════════════════════════════════
// COMPONENTE: Navbar de AstroEngine PRO
// ═══════════════════════════════════════════════════════════════
function AENavbar() {
  const handleDocsClick = () => {
    alert(MODULE_DOCS);
  };

  const navStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    zIndex: 9999,
    background: 'rgba(26, 26, 46, 0.98)',
    backdropFilter: 'blur(20px)',
    borderBottom: '1px solid rgba(233, 69, 96, 0.2)',
    padding: '12px 20px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontFamily: "'Inter', sans-serif"
  };

  const leftStyle = { display: 'flex', alignItems: 'center', gap: '15px' };
  
  const logoStyle = {
    color: '#e94560',
    fontSize: '1.1rem',
    fontWeight: 700,
    textDecoration: 'none',
    display: 'flex',
    alignItems: 'center',
    gap: '6px'
  };

  const proStyle = {
    fontWeight: 400,
    fontSize: '0.65rem',
    background: 'linear-gradient(135deg, #e94560, #f5a623)',
    color: '#fff',
    padding: '2px 6px',
    borderRadius: '4px',
    marginLeft: '6px'
  };

  const dividerStyle = { width: '1px', height: '20px', background: 'rgba(233, 69, 96, 0.3)' };
  const moduleStyle = { color: '#888', fontSize: '0.85rem' };
  const rightStyle = { display: 'flex', gap: '8px' };

  const btnBase = {
    padding: '8px 14px',
    borderRadius: '6px',
    fontSize: '0.8rem',
    textDecoration: 'none',
    cursor: 'pointer',
    fontWeight: 500,
    transition: 'all 0.2s'
  };

  const btnSecondary = {
    ...btnBase,
    background: 'transparent',
    color: '#888',
    border: '1px solid rgba(233, 69, 96, 0.3)'
  };

  const btnPrimary = {
    ...btnBase,
    background: 'linear-gradient(135deg, #e94560, #f5a623)',
    color: '#fff',
    border: 'none'
  };

  const volverHref = urlParams.celebData
    ? `${HUB_URL}/?celeb=${urlParams.demo}#demo`
    : (urlParams.userData ? DASHBOARD_URL : null);

  return (
    <div style={navStyle}>
      <div style={leftStyle}>
        <a href={HUB_URL} style={logoStyle}>
          ✦ AstroEngine<span style={proStyle}>PRO</span>
        </a>
        <div style={dividerStyle}></div>
        <span style={moduleStyle}>Regencias Planetarias</span>
        {volverHref && urlParams.celebData && (
          <a href={volverHref} style={{...btnSecondary, marginLeft: '10px'}}>← Volver a {urlParams.activeData.nombre}</a>
        )}
      </div>
      <div style={rightStyle}>
        <a href={HUB_URL} style={btnSecondary}>← Hub</a>
        <button onClick={handleDocsClick} style={btnSecondary}>Ayuda</button>
        {urlParams.celebData ? (
          <a href={LOGIN_URL} style={btnPrimary}>Usar con mis clientes</a>
        ) : urlParams.userData ? (
          <>
            <a href={(function(){ const p = new URLSearchParams(); const clienteId = new URLSearchParams(window.location.search).get('cliente_id'); if (clienteId) p.set('cliente', clienteId); if (urlParams.userData.email) p.set('email', urlParams.userData.email); const qs = p.toString(); return DASHBOARD_URL + (qs ? '?' + qs : ''); })()} style={btnPrimary}>Mi Dashboard</a>
            <button onClick={() => { localStorage.removeItem('astroengine_session'); window.location.href = HUB_URL; }} style={{...btnSecondary, fontSize: '0.8rem', cursor: 'pointer'}}>Cerrar sesión</button>
          </>
        ) : (
          <a href={LOGIN_URL} style={btnPrimary}>Iniciar sesión</a>
        )}
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// COMPONENTE: Banner de Demo
// ═══════════════════════════════════════════════════════════════
function AEDemoBanner({ celebName }) {
  const bannerStyle = {
    position: 'fixed',
    top: '52px',
    left: 0,
    right: 0,
    zIndex: 9998,
    background: 'rgba(245, 158, 11, 0.12)',
    borderBottom: '1px solid rgba(245, 158, 11, 0.25)',
    padding: '10px 20px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '15px',
    flexWrap: 'wrap',
    fontFamily: "'Inter', sans-serif"
  };

  const textStyle = { color: '#fbbf24', fontSize: '0.85rem' };
  const celebStyle = { color: '#fff', fontWeight: 600 };

  const btnStyle = {
    padding: '6px 12px',
    borderRadius: '6px',
    fontSize: '0.75rem',
    background: 'linear-gradient(135deg, #e94560, #f5a623)',
    color: '#fff',
    textDecoration: 'none',
    fontWeight: 500
  };

  const isDemoMode = !!urlParams.celebData;

  return (
    <div style={bannerStyle}>
      {isDemoMode ? (
        <>
          <span style={textStyle}>
            🔒 Demo para <span style={celebStyle}>{celebName}</span>.
          </span>
          <a href={LOGIN_URL} style={btnStyle}>Elegir plan</a>
        </>
      ) : (
        <span style={textStyle}>
          ✅ Datos cargados de <span style={celebStyle}>{celebName}</span>.
        </span>
      )}
    </div>
  );
}


// ═══════════════════════════════════════════════════════════════
// COMPONENTE: Acceso requerido
// ═══════════════════════════════════════════════════════════════
function AEAccessRequired() {
  const cardStyle = {
    maxWidth: '520px',
    margin: '120px auto 0',
    background: 'rgba(26,26,46,.9)',
    border: '1px solid rgba(233,69,96,.35)',
    borderRadius: '22px',
    padding: '2.2rem',
    textAlign: 'center',
    color: '#f0ebe3',
    boxShadow: '0 24px 70px rgba(0,0,0,.45)'
  };

  const btnPrimary = {
    display: 'block',
    padding: '.9rem 1.1rem',
    borderRadius: '10px',
    textDecoration: 'none',
    fontWeight: 700,
    background: 'linear-gradient(135deg,#e94560,#f5a623)',
    color: '#fff',
    marginTop: '.8rem'
  };

  const btnSecondary = {
    ...btnPrimary,
    background: 'transparent',
    color: '#f5a623',
    border: '1px solid rgba(245,166,35,.35)'
  };

  return (
    <div style={cardStyle}>
      <h2 style={{ color: '#f5a623', marginBottom: '.8rem' }}>Cuenta activa requerida</h2>
      <p style={{ color: '#aaa', lineHeight: 1.6 }}>
        Este módulo forma parte de AstroEngine PRO. Para usarlo con tus datos necesitas iniciar sesión con tu email y código de acceso.
      </p>
      <a href={LOGIN_URL} style={btnPrimary}>Iniciar sesión</a>
      <a href={`${HUB_URL}#demo`} style={btnSecondary}>Ver demos públicos</a>
      <a href={HUB_URL} style={btnSecondary}>Volver al Hub</a>
    </div>
  );
}


function App() {
  const isLockedNatal = !!urlParams.activeData && !urlParams.admin;

  // Valores iniciales según si es demo o no
  const getInitialFormState = () => {
    if (urlParams.activeData) {
      const c = urlParams.activeData;
      return {
        fecha: c.fecha,
        hora: c.hora,
        nombre: c.nombre,
        latitud: c.lat,
        longitud: c.lon,
        timezone: c.tz,
        ciudadSeleccionada: c.pais ? `${c.ciudad}, ${c.pais}` : c.ciudad
      };
    }
    return {
      fecha: '',
      hora: '',
      nombre: '',
      latitud: null,
      longitud: null,
      timezone: null,
      ciudadSeleccionada: ''
    };
  };

  const [formulario, setFormulario] = useState(getInitialFormState());
  
  const [resultado, setResultado] = useState(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState(null);
  const [vista, setVista] = useState('cliente');

  // Estados para el buscador de ciudades
  const [busquedaCiudad, setBusquedaCiudad] = useState('');
  const [sugerencias, setSugerencias] = useState([]);
  const [buscandoCiudad, setBuscandoCiudad] = useState(false);
  const [mostrarSugerencias, setMostrarSugerencias] = useState(false);
  const buscadorRef = useRef(null);
  const timeoutRef = useRef(null);

  // NO auto-calcular - dejamos que el usuario pulse el botón

  // Cerrar sugerencias al hacer click fuera
  useEffect(() => {
    const handleClickFuera = (e) => {
      if (buscadorRef.current && !buscadorRef.current.contains(e.target)) {
        setMostrarSugerencias(false);
      }
    };
    document.addEventListener('mousedown', handleClickFuera);
    return () => document.removeEventListener('mousedown', handleClickFuera);
  }, []);

  // ═══ RETURN CONDICIONAL DESPUÉS DE TODOS LOS HOOKS ═══
  if (!urlParams.activeData) {
    return (
      <>
        <AENavbar />
        <AEAccessRequired />
      </>
    );
  }

  // Buscar ciudades con debounce
  const handleBuscarCiudad = (texto) => {
    setBusquedaCiudad(texto);
    
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    
    if (texto.length < 2) {
      setSugerencias([]);
      setMostrarSugerencias(false);
      return;
    }

    timeoutRef.current = setTimeout(async () => {
      setBuscandoCiudad(true);
      try {
        const response = await fetch(`${GEOCODING_URL}?q=${encodeURIComponent(texto)}&limit=5`);
        if (response.ok) {
          const data = await response.json();
          setSugerencias(data.results || []);
          setMostrarSugerencias(true);
        }
      } catch (err) {
        console.error('Error buscando ciudad:', err);
        setSugerencias([]);
      } finally {
        setBuscandoCiudad(false);
      }
    }, 400);
  };

  // Seleccionar una ciudad de las sugerencias
  const handleSeleccionarCiudad = (ciudad) => {
    setFormulario({
      ...formulario,
      ciudadSeleccionada: ciudad.nombre,
      latitud: ciudad.latitud,
      longitud: ciudad.longitud,
      timezone: ciudad.timezone
    });
    setBusquedaCiudad('');
    setSugerencias([]);
    setMostrarSugerencias(false);
  };

  // Seleccionar ciudad de botones rápidos
  const handleCiudadChange = (ciudad) => {
    if (urlParams.readonly) return; // Bloquear en modo readonly
    const c = CIUDADES.find(x => x.nombre === ciudad);
    if (c) {
      setFormulario({
        ...formulario,
        ciudadSeleccionada: ciudad,
        latitud: c.lat,
        longitud: c.lon,
        timezone: c.tz
      });
      setBusquedaCiudad('');
      setSugerencias([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setCargando(true);
    setError(null);
    
    try {
      const [year, month, day] = formulario.fecha.split('-');
      const fechaFormateada = `${day}/${month}/${year}`;
      
      // ═══ MODO DEMO: servir JSON pregrabado, nunca tocar el API real ═══
      if (urlParams.celebData) {
        const jsonResp = await fetch(`demo/${urlParams.demo}-regencias.json`);
        if (!jsonResp.ok) throw new Error('Demo JSON no encontrado');
        const demoData = await jsonResp.json();
        await new Promise(r => setTimeout(r, 400));
        setResultado(demoData);
        setCargando(false);
        return;
      }

      // ═══ CACHE: clave única por parámetros ═══
      const cacheKey = `regencias_cache_${formulario.fecha}_${formulario.hora}_${formulario.latitud}_${formulario.longitud}`;
      const cached = localStorage.getItem(cacheKey);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        const horasTranscurridas = (Date.now() - timestamp) / (1000 * 60 * 60);
        if (horasTranscurridas < 24) {
          setResultado(data);
          setCargando(false);
          return;
        }
      }

      const response = await fetch(API_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          fecha: fechaFormateada,
          hora: formulario.hora,
          latitud: formulario.latitud,
          longitud: formulario.longitud,
          timezone: formulario.timezone,
          nombre: formulario.nombre || 'Consultante'
        })
      });
      
      if (!response.ok) {
        const errData = await response.json();
        throw new Error(errData.detail || 'Error en el servidor');
      }
      
      const responseData = await response.json();
      
      if (responseData.success && responseData.data) {
        // Guardar en cache con timestamp
        localStorage.setItem(cacheKey, JSON.stringify({
          data: responseData.data,
          timestamp: Date.now()
        }));
        setResultado(responseData.data);
      } else {
        throw new Error('Respuesta inválida del servidor');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    } finally {
      setCargando(false);
    }
  };

  const handleNuevaConsulta = () => {
    setResultado(null);
    setError(null);
  };

  // Estilo del contenedor principal con margen para navbar
  const containerStyle = {
    paddingTop: urlParams.celebData ? '115px' : '70px'
  };

  // FORMULARIO
  if (!resultado) {
    return (
      <>
        <AENavbar />
        {urlParams.activeData && <AEDemoBanner celebName={urlParams.activeData.nombre} />}
        
        <div className="container" style={containerStyle}>
          <h1>Regencias Planetarias</h1>
          <p className="subtitle">Almuten Figuris y Máximo Dispositor</p>

          <form onSubmit={handleSubmit} className="formulario no-print">
            {error && <div className="error">{error}</div>}
            
            <div className="form-group">
              <label>Nombre (opcional)</label>
              <input
                type="text"
                value={formulario.nombre}
                onChange={(e) => setFormulario({...formulario, nombre: e.target.value})}
                placeholder="Nombre del consultante"
                disabled={isLockedNatal}
                readOnly={isLockedNatal}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Fecha de nacimiento</label>
                <input
                  type="date"
                  value={formulario.fecha}
                  onChange={(e) => setFormulario({...formulario, fecha: e.target.value})}
                  required
                  disabled={isLockedNatal}
                  readOnly={isLockedNatal}
                />
              </div>
              <div className="form-group">
                <label>Hora de nacimiento</label>
                <input
                  type="time"
                  value={formulario.hora}
                  onChange={(e) => setFormulario({...formulario, hora: e.target.value})}
                  required
                  disabled={isLockedNatal}
                  readOnly={isLockedNatal}
                />
              </div>
            </div>

            <div className="form-group">
              <label>Ciudad de nacimiento</label>
              
              {/* Buscador */}
              {!isLockedNatal && (
                <div className="buscador-ciudad" ref={buscadorRef}>
                  <input
                    type="text"
                    className="ciudad-search-input"
                    placeholder="Buscar cualquier ciudad..."
                    value={busquedaCiudad}
                    onChange={(e) => handleBuscarCiudad(e.target.value)}
                  />
                  {buscandoCiudad && <span className="buscando-indicator">Buscando...</span>}
                  
                  {mostrarSugerencias && sugerencias.length > 0 && (
                    <div className="sugerencias-lista">
                      {sugerencias.map((sug, idx) => (
                        <div
                          key={idx}
                          className="sugerencia-item"
                          onClick={() => handleSeleccionarCiudad(sug)}
                        >
                          <span className="sugerencia-nombre">{sug.nombre}</span>
                          <span className="sugerencia-coords">
                            {sug.latitud.toFixed(2)}°, {sug.longitud.toFixed(2)}°
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Ciudad seleccionada */}
              {formulario.ciudadSeleccionada && (
                <div className="ciudad-seleccionada">
                  📍 {formulario.ciudadSeleccionada} ({formulario.latitud.toFixed(4)}°, {formulario.longitud.toFixed(4)}°) UTC{formulario.timezone >= 0 ? '+' : ''}{formulario.timezone}
                </div>
              )}

              {/* Botones rápidos */}
              {!isLockedNatal && (
                <div className="ciudades-grid">
                  {CIUDADES.map(c => (
                    <button
                      key={c.nombre}
                      type="button"
                      className={`ciudad-btn ${formulario.ciudadSeleccionada === c.nombre ? 'active' : ''}`}
                      onClick={() => handleCiudadChange(c.nombre)}
                    >
                      {c.nombre}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button type="submit" className="submit-btn" disabled={cargando}>
              {cargando ? '⏳ Calculando...' : '✨ Calcular Regencias'}
            </button>
          </form>
        </div>
      </>
    );
  }

  // RESULTADOS - Lectura correcta de la estructura del API
  // Almuten Figuris
  const almutenNombre = resultado.almuten_figuris?.planeta;
  const almutenSimbolo = resultado.almuten_figuris?.simbolo || SIMBOLOS[almutenNombre] || '?';
  const almutenPuntos = resultado.almuten_figuris?.puntuacion; // API usa "puntuacion", no "puntos"
  const almutenInterp = INTERPRETACIONES[almutenNombre];
  const almutenDesglose = resultado.almuten_figuris?.desglose || [];
  const almutenRanking = resultado.almuten_figuris?.ranking || [];

  // Máximo Dispositor - El API tiene dos métodos, usamos metodo_convergencia (Tradición Clásica)
  const metodoEstricto = resultado.maximo_dispositor?.metodo_estricto || {};
  const metodoConvergencia = resultado.maximo_dispositor?.metodo_convergencia || {};
  
  // Usamos el método de convergencia como principal, con fallback al estricto
  const dispositorNombre = metodoConvergencia.planeta || metodoEstricto.planeta;
  const dispositorSimbolo = metodoConvergencia.simbolo || metodoEstricto.simbolo || SIMBOLOS[dispositorNombre] || '🔄';
  const dispositorInterp = INTERP_DISPOSITOR[dispositorNombre];
  const dispositorRecibeDe = metodoConvergencia.recibe_de || [];
  const dispositorDesc = metodoConvergencia.descripcion || metodoEstricto.descripcion || '';

  // Bucle mutuo - viene del método estricto
  const hayBucle = metodoEstricto.hay_bucle_mutuo || false;
  const bucleDesc = hayBucle 
    ? `Recepción mutua entre: ${(metodoEstricto.planetas_en_bucle || []).join(' ↔ ')}`
    : '';

  // Cadena de disposiciones - está dentro de maximo_dispositor
  const cadena = resultado.maximo_dispositor?.cadena || {};

  // Regente del ASC - estructura correcta del API
  const signoAsc = resultado.regente_asc?.signo_asc || '-';
  const regenteNombre = resultado.regente_asc?.planeta || '-';
  const regenteSimbolo = resultado.regente_asc?.simbolo || SIMBOLOS[regenteNombre] || '?';

  return (
    <>
      <AENavbar />
      {urlParams.activeData && <AEDemoBanner celebName={urlParams.activeData.nombre} />}
      
      <div className="container" style={containerStyle}>
        <h1>Regencias Planetarias</h1>
        <p className="subtitle">Almuten Figuris y Máximo Dispositor</p>

        {/* Datos de entrada */}
        <div className="datos-entrada">
          <div><strong>{formulario.nombre || 'Consultante'}</strong></div>
          <div>📅 {formulario.fecha} a las {formulario.hora}</div>
          <div>📍 {formulario.ciudadSeleccionada}</div>
        </div>

        {/* Toggle y botones */}
        <div className="toggle-container no-print">
          <div className="toggle-tabs">
            <button 
              className={`toggle-tab ${vista === 'cliente' ? 'active' : ''}`}
              onClick={() => setVista('cliente')}
            >
              👤 Vista Cliente
            </button>
            <button 
              className={`toggle-tab ${vista === 'tecnico' ? 'active' : ''}`}
              onClick={() => setVista('tecnico')}
            >
              📊 Vista Técnica
            </button>
          </div>
          {!isLockedNatal && (
            <button className="nueva-btn" onClick={handleNuevaConsulta}>🔄 Nueva</button>
          )}
        </div>

        {/* VISTA CLIENTE */}
        {vista === 'cliente' && (
          <div className="vista-cliente">
            <div className="resumen-rapido">
              <div className="resumen-item">
                <div className="symbol">{regenteSimbolo}</div>
                <div className="name">{regenteNombre}</div>
                <div className="role">Regente ASC ({signoAsc})</div>
              </div>
              <div className="resumen-item destacado">
                <div className="symbol">{almutenSimbolo}</div>
                <div className="name">{almutenNombre}</div>
                <div className="role">Almuten ({almutenPuntos} pts)</div>
              </div>
              <div className="resumen-item">
                <div className="symbol">{dispositorSimbolo}</div>
                <div className="name">{dispositorNombre || 'Bucle mutuo'}</div>
                <div className="role">Dispositor</div>
              </div>
            </div>

            {almutenInterp && (
              <div className="card">
                <div className="card-header">
                  <div className="planet-symbol">{almutenSimbolo}</div>
                  <div className="card-title">
                    <h2>{almutenNombre} <span className="puntos">{almutenPuntos} puntos</span></h2>
                    <div className="label">Tu Almuten Figuris - "{almutenInterp.titulo}"</div>
                  </div>
                </div>
                <div className="interpretacion">
                  <h4>Rasgos principales</h4>
                  <p>{almutenInterp.rasgos}</p>
                  <div className="vocacion">
                    <h4>Vocación natural</h4>
                    <p>{almutenInterp.vocacion}</p>
                  </div>
                </div>
              </div>
            )}

            <div className="card">
              <div className="card-header">
                <div className="planet-symbol">{dispositorSimbolo}</div>
                <div className="card-title">
                  <h2>{dispositorNombre || 'Recepción Mutua'}</h2>
                  <div className="label">
                    {dispositorNombre 
                      ? `Tu Máximo Dispositor - "${INTERPRETACIONES[dispositorNombre]?.titulo || ''}"` 
                      : bucleDesc}
                  </div>
                </div>
              </div>
              <div className="interpretacion">
                <h4>¿Qué significa?</h4>
                <p><strong>{dispositorInterp || bucleDesc}</strong></p>
                {dispositorNombre && dispositorRecibeDe.length > 0 && (
                  <p style={{marginTop: '10px', color: '#888'}}>
                    Recibe energía de: {dispositorRecibeDe.join(', ')}
                  </p>
                )}
                {hayBucle && (
                  <p style={{marginTop: '10px', color: '#f5a623'}}>
                    Nota: {bucleDesc}
                  </p>
                )}
              </div>
            </div>
          </div>
        )}

        {/* VISTA TÉCNICA */}
        {vista === 'tecnico' && (
          <div className="vista-tecnico">
            <div className="resultado-tecnico">
              <div className="resultado-item ganador">
                <span className="resultado-label">ALMUTEN FIGURIS</span>
                <span className="resultado-valor">{almutenSimbolo} {almutenNombre}</span>
                <span className="resultado-puntos">{almutenPuntos} puntos</span>
              </div>
              <div className="resultado-item">
                <span className="resultado-label">DISPOSITOR FINAL</span>
                <span className="resultado-valor">{dispositorSimbolo} {dispositorNombre || 'Bucle'}</span>
                {dispositorRecibeDe.length > 0 && (
                  <span className="resultado-puntos">recibe de {dispositorRecibeDe.length}</span>
                )}
              </div>
              <div className="resultado-item">
                <span className="resultado-label">REGENTE ASC</span>
                <span className="resultado-valor">{regenteSimbolo} {regenteNombre}</span>
                <span className="resultado-puntos">{signoAsc}</span>
              </div>
            </div>

            <h3 className="seccion-titulo">📊 Desglose de Dignidades (Ibn Ezra / Morinus)</h3>
            
            <table className="tabla-tecnica">
              <thead>
                <tr>
                  <th>Punto</th>
                  <th>Posición</th>
                  <th>☉</th>
                  <th>☽</th>
                  <th>☿</th>
                  <th>♀</th>
                  <th>♂</th>
                  <th>♃</th>
                  <th>♄</th>
                </tr>
              </thead>
              <tbody>
                {almutenDesglose.map((punto, idx) => {
                  const digs = punto.dignidades || {};
                  return (
                    <tr key={idx}>
                      <td className="punto-nombre">{punto.punto}</td>
                      <td className="posicion">{punto.posicion}</td>
                      <td>{formatDignidad(digs['Sol'])}</td>
                      <td>{formatDignidad(digs['Luna'])}</td>
                      <td>{formatDignidad(digs['Mercurio'])}</td>
                      <td>{formatDignidad(digs['Venus'])}</td>
                      <td>{formatDignidad(digs['Marte'])}</td>
                      <td>{formatDignidad(digs['Júpiter'])}</td>
                      <td>{formatDignidad(digs['Saturno'])}</td>
                    </tr>
                  );
                })}
                <tr className="total-row">
                  <td className="punto-nombre"><strong>TOTAL</strong></td>
                  <td></td>
                  {['Sol', 'Luna', 'Mercurio', 'Venus', 'Marte', 'Júpiter', 'Saturno'].map(planeta => {
                    const item = almutenRanking.find(r => r.planeta === planeta);
                    const total = item?.puntos || 0;
                    const esGanador = planeta === almutenNombre;
                    return (
                      <td key={planeta} className={esGanador ? 'winner' : ''}>
                        {total}{esGanador && ' 🏆'}
                      </td>
                    );
                  })}
                </tr>
              </tbody>
            </table>

            <div className="leyenda-dignidades">
              <span><span className="dom">■</span> Domicilio (5)</span>
              <span><span className="exa">■</span> Exaltación (4)</span>
              <span><span className="tri">■</span> Triplicidad (3)</span>
              <span><span className="ter">■</span> Término (2)</span>
              <span><span className="faz">■</span> Faz (1)</span>
            </div>

            <div className="cadena-container">
              <h3 className="seccion-titulo">🔗 Cadena de Disposiciones</h3>
              <div className="cadena-visual">
                {Object.entries(cadena).map(([planeta, dispone]) => (
                  <div key={planeta} className="cadena-linea">
                    <span className="cadena-planeta">{SIMBOLOS[planeta]} {planeta}</span>
                    <span className="cadena-flecha">→</span>
                    <span className={`cadena-planeta ${dispone === dispositorNombre ? 'hub' : ''}`}>
                      {SIMBOLOS[dispone]} {dispone}
                    </span>
                  </div>
                ))}
              </div>
              {hayBucle && (
                <p className="metodo-info">⚠️ {bucleDesc}</p>
              )}
              <p className="metodo-info">
                ✅ Método Tradición Clásica: {dispositorDesc}
              </p>
            </div>

            <div className="ranking-container">
              <h3 className="seccion-titulo">🏅 Ranking de Planetas</h3>
              <div className="ranking-grid">
                {almutenRanking.map((r, idx) => (
                  <div key={r.planeta} className={`ranking-item ${idx === 0 ? 'primero' : ''}`}>
                    <span className="ranking-pos">#{idx + 1}</span>
                    <span className="ranking-planeta">{r.simbolo} {r.planeta}</span>
                    <span className="ranking-puntos">{r.puntos} pts</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}

// Función para formatear dignidades en la tabla
function formatDignidad(dig) {
  if (!dig) return '-';
  const partes = [];
  if (dig.domicilio) partes.push(<span key="d" className="dom">{dig.domicilio}</span>);
  if (dig.exaltacion) partes.push(<span key="e" className="exa">+{dig.exaltacion}</span>);
  if (dig.triplicidad) partes.push(<span key="t" className="tri">{dig.triplicidad}</span>);
  if (dig.termino) partes.push(<span key="r" className="ter">{dig.termino}</span>);
  if (dig.faz) partes.push(<span key="f" className="faz">+{dig.faz}</span>);
  return partes.length > 0 ? partes : '-';
}

export default App;
